// Master-password rotation orchestration.
//
// Pure TS-layer composition over existing worker primitives:
// `openOpfsVault` / `openS3Vault` (per synced vault), `changePassphrase`
// (per vault and the master), `lockVault` (best-effort), and
// `saveAppConfig` (final commit). No new Rust code.
//
// Why so little Rust? The keybag's wrapping is what rotates — its
// contents (`metadata_key`, `mutable_index_key`, `volume_id`) are
// stable. `app_config_key = HKDF(metadata_key, "vaultwisp/app-config/v1")`
// therefore stays constant across a master rotation: every vault's
// `app_config_key` is unchanged and `/system/config.enc` is rewritten
// under the same AEAD key with new plaintext content (clearing or
// setting `pending_rotation` markers).
//
// Current-master verification: master plaintext is gone after unlock
// by design (see `masterSession.ts`). We re-verify by calling
// `changePassphrase(master, currentBytes, currentBytes)` — a no-op
// re-wrap that throws "wrong password" if the user mistyped. Simpler
// than spinning up a scratch handle.
//
// Retry simplification (PR 5a): pending rotations record
// `old_password_sealed_hex: ""` (sentinel for "re-prompt required").
// The full sealed-old-password optimization in the design spec is
// deferred — it would need a new Rust AEAD primitive.

import type * as Comlink from "comlink";
import type {
  AppConfig,
  S3ConfigFull,
  VaultEntry,
  VaultWorkerApi,
} from "../worker/api";
import { useMasterSession } from "../stores/masterSession";
import { toPassphraseBytes, zeroize } from "./passphraseBytes";

// ————— Public types ——————————————————————————————————————————

export type AbortReason =
  | "wrong-current-master"
  | "master-rotation-failed"
  | "save-config-failed";

export type RotationPhase =
  | { kind: "verifying-current" }
  | {
      kind: "rotating-vault";
      vaultId: string;
      displayName: string;
      index: number;
      total: number;
    }
  | { kind: "rotating-master" }
  | { kind: "saving-config" }
  | { kind: "done" }
  | { kind: "aborted"; reason: AbortReason; error: string };

export interface RotationResultRow {
  vaultId: string;
  displayName: string;
  outcome: "pending" | "success" | "failure";
  error?: string;
}

export interface RotationProgress {
  phase: RotationPhase;
  results: RotationResultRow[];
}

export interface RotationReport {
  rotatedCount: number;
  pendingCount: number;
  masterRotated: boolean;
}

export interface RotationOpts {
  onProgress?: (p: RotationProgress) => void;
  /**
   * When non-empty, restrict the retry loop to vaults whose id is
   * in this list. Used by the per-vault "Retry pending rotation"
   * button in `VaultsTab`. Ignored by `changeMaster`.
   */
  vaultIds?: string[];
}

// ————— Internal helpers ——————————————————————————————————————

/**
 * Subset of `VaultWorkerApi` actually used by the rotation
 * orchestrator. Accepts both the bare `VaultWorkerApi` (for tests
 * that supply a plain stub) and the `Comlink.Remote<VaultWorkerApi>`
 * the React app sees through `useVault()`. Using `Remote<T>` keeps
 * call-site type checking honest about awaiting remote calls.
 */
export type WorkerSubset =
  | Pick<
      VaultWorkerApi,
      | "openOpfsVault"
      | "openS3Vault"
      | "changePassphrase"
      | "lockVault"
      | "saveAppConfig"
    >
  | Pick<
      Comlink.Remote<VaultWorkerApi>,
      | "openOpfsVault"
      | "openS3Vault"
      | "changePassphrase"
      | "lockVault"
      | "saveAppConfig"
    >;

/**
 * The master vault is the OPFS-root vault that hosts
 * `/system/config.enc`. `MasterSetupWizard` always creates it at
 * subdir "" — that invariant is what we filter on (NOT the
 * `primary_vault_id`, which the user can reassign in settings without
 * changing the master).
 */
function isMasterEntry(v: VaultEntry): boolean {
  return v.adapter.type === "opfs" && v.adapter.subdir === "";
}

function s3FromEntry(entry: VaultEntry): S3ConfigFull {
  if (entry.adapter.type !== "s3-compat") {
    throw new Error(`expected s3-compat adapter on ${entry.id}`);
  }
  const a = entry.adapter;
  return {
    endpoint: a.endpoint,
    region: a.region,
    bucket: a.bucket,
    prefix: a.prefix,
    urlStyle: a.url_style === "virtual-hosted" ? "virtual-host" : "path",
    accessKeyId: a.access_key_id,
    secretAccessKey: a.secret_access_key,
  };
}

function emit(
  opts: RotationOpts,
  phase: RotationPhase,
  results: RotationResultRow[],
): void {
  opts.onProgress?.({ phase, results: results.map((r) => ({ ...r })) });
}

/**
 * Rotate a single synced non-master vault: open with `oldBytes`,
 * `changePassphrase` to `newBytes`, lockVault best-effort. Throws on
 * the open or the changePassphrase failure; lockVault failures are
 * swallowed.
 */
async function rotateOneVault(
  worker: WorkerSubset,
  entry: VaultEntry,
  oldBytes: Uint8Array,
  newBytes: Uint8Array,
): Promise<void> {
  let handle: number;
  if (entry.adapter.type === "opfs") {
    handle = await worker.openOpfsVault(entry.adapter.subdir, oldBytes);
  } else {
    handle = await worker.openS3Vault(s3FromEntry(entry), oldBytes);
  }
  try {
    await worker.changePassphrase(handle, oldBytes, newBytes);
  } finally {
    await worker.lockVault(handle).catch(() => {
      /* best-effort */
    });
  }
}

/**
 * Drive the per-vault rotation loop. `targets` are the synced
 * non-master vault entries to attempt. Updates `results` in place
 * with success / failure outcomes. Emits onProgress BEFORE each
 * vault starts AND once AFTER the row outcome is recorded so the
 * modal can render each ✓/✗ as it lands (including the last vault,
 * which has no "next vault" event to piggyback on).
 */
async function rotateAllTargets(
  worker: WorkerSubset,
  targets: VaultEntry[],
  oldBytes: Uint8Array,
  newBytes: Uint8Array,
  results: RotationResultRow[],
  opts: RotationOpts,
): Promise<void> {
  for (let i = 0; i < targets.length; i += 1) {
    const entry = targets[i];
    emit(
      opts,
      {
        kind: "rotating-vault",
        vaultId: entry.id,
        displayName: entry.display_name,
        index: i,
        total: targets.length,
      },
      results,
    );
    const row = results.find((r) => r.vaultId === entry.id);
    if (!row) continue;
    try {
      await rotateOneVault(worker, entry, oldBytes, newBytes);
      row.outcome = "success";
    } catch (err) {
      row.outcome = "failure";
      row.error = err instanceof Error ? err.message : String(err);
    }
    // Post-vault emission: re-emit the same `rotating-vault` phase
    // shape with the now-updated `results` array so the UI can flip
    // this row's badge from `…` to ✓/✗ before the next vault starts
    // (or before the loop exits to `rotating-master` / `saving-config`).
    emit(
      opts,
      {
        kind: "rotating-vault",
        vaultId: entry.id,
        displayName: entry.display_name,
        index: i,
        total: targets.length,
      },
      results,
    );
  }
}

/**
 * Build the updated AppConfig given the rotation outcome rows. Every
 * row vault has its `pending_rotation` set or cleared; rows not in
 * the rotation set leave their entry untouched.
 */
function applyRowsToConfig(
  cfg: AppConfig,
  rows: RotationResultRow[],
  startedAt: number,
): AppConfig {
  const byId = new Map(rows.map((r) => [r.vaultId, r]));
  return {
    ...cfg,
    vaults: cfg.vaults.map((v) => {
      const row = byId.get(v.id);
      if (!row) return v;
      if (row.outcome === "success") {
        return { ...v, pending_rotation: null };
      }
      // Failure or still-pending → preserve / set the pending marker.
      return {
        ...v,
        pending_rotation: {
          old_password_sealed_hex: "",
          nonce_hex: "",
          started_at: startedAt,
        },
      };
    }),
  };
}

// ————— Public API ——————————————————————————————————————————

/**
 * Change the master password. Verifies `currentPass` first, rotates
 * every synced non-master vault, rotates the master vault itself,
 * then atomically commits the updated AppConfig with per-vault
 * `pending_rotation` markers for any failures.
 *
 * Throws if (a) no master session is unlocked, (b) `currentPass` is
 * wrong, (c) the master rotation itself fails, or (d) the final
 * `saveAppConfig` fails. In cases (c) and (d) any vaults that
 * already rotated stay rotated — the caller surfaces this as a
 * partial-failure to the user.
 */
export async function changeMaster(
  worker: WorkerSubset,
  currentPass: string,
  newPass: string,
  opts: RotationOpts,
): Promise<RotationReport> {
  const master = useMasterSession.getState().master;
  if (!master?.appConfig) {
    throw new Error("no master session unlocked");
  }

  const currentBytes = toPassphraseBytes(currentPass);
  const newBytes = toPassphraseBytes(newPass);
  const startedAt = Math.floor(Date.now() / 1000);

  try {
    // ————— Step 1: verify current master ——————————————————
    emit(opts, { kind: "verifying-current" }, []);
    // No-op re-wrap; throws "wrong password" if currentBytes is wrong.
    try {
      await worker.changePassphrase(
        master.masterHandle,
        currentBytes,
        currentBytes,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      emit(
        opts,
        { kind: "aborted", reason: "wrong-current-master", error: message },
        [],
      );
      throw err;
    }

    // ————— Step 2: build target list (synced, non-master) ——
    //
    // The master vault is the OPFS-root vault that hosts
    // `/system/config.enc` — it's rotated separately in step 4 via
    // the masterHandle. Identify it by adapter shape rather than by
    // `primary_vault_id` because the user can reassign the primary in
    // settings (locked decision); the master always lives at OPFS
    // subdir "".
    const cfg = master.appConfig;
    const targets = cfg.vaults.filter(
      (v) => v.sync_state === "synced" && !isMasterEntry(v),
    );
    const results: RotationResultRow[] = targets.map((v) => ({
      vaultId: v.id,
      displayName: v.display_name,
      outcome: "pending",
    }));

    // ————— Step 3: rotate each synced non-master vault ————
    await rotateAllTargets(
      worker,
      targets,
      currentBytes,
      newBytes,
      results,
      opts,
    );

    // ————— Step 4: rotate the master vault ————————————————
    emit(opts, { kind: "rotating-master" }, results);
    try {
      await worker.changePassphrase(
        master.masterHandle,
        currentBytes,
        newBytes,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      emit(
        opts,
        { kind: "aborted", reason: "master-rotation-failed", error: message },
        results,
      );
      throw err;
    }

    return await commitRotationResults({
      worker,
      master,
      cfg,
      results,
      startedAt,
      opts,
      masterRotated: true,
    });
  } finally {
    zeroize(currentBytes);
    zeroize(newBytes);
  }
}

/**
 * Persist rotation results to the encrypted app-config and emit the
 * terminal `saving-config` → `done` progress events. Both
 * `changeMaster` and `retryPendingRotations` end with this same
 * sequence — the only difference is whether the master itself
 * rotated (`changeMaster: true`, `retry: false`). On `saveAppConfig`
 * failure we emit `aborted(save-config-failed)` BEFORE rethrowing so
 * any UI driving the rotation transitions out of `saving-config`
 * into a closable state instead of getting stuck on the spinner.
 */
async function commitRotationResults(args: {
  worker: WorkerSubset;
  master: NonNullable<ReturnType<typeof useMasterSession.getState>["master"]>;
  cfg: AppConfig;
  results: RotationResultRow[];
  startedAt: number;
  opts: RotationOpts;
  masterRotated: boolean;
}): Promise<RotationReport> {
  const { worker, master, cfg, results, startedAt, opts, masterRotated } = args;

  emit(opts, { kind: "saving-config" }, results);
  const updated = applyRowsToConfig(cfg, results, startedAt);
  try {
    await worker.saveAppConfig(master.masterHandle, updated);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    emit(
      opts,
      { kind: "aborted", reason: "save-config-failed", error: message },
      results,
    );
    throw err;
  }
  useMasterSession.getState().setAppConfig(updated);

  emit(opts, { kind: "done" }, results);

  const rotatedCount = results.filter((r) => r.outcome === "success").length;
  const pendingCount = results.filter((r) => r.outcome === "failure").length;
  return { rotatedCount, pendingCount, masterRotated };
}

/**
 * Retry every synced vault whose `pending_rotation` is non-null. Uses
 * `oldMaster` to open each vault and `currentMaster` (the user's
 * current master, post a successful master rotation) to re-wrap.
 *
 * The master vault itself is NOT rotated here — that already happened
 * during the initial `changeMaster` call. This is purely a retry-loop
 * over the failed-during-rotation tail.
 */
export async function retryPendingRotations(
  worker: WorkerSubset,
  oldMaster: string,
  currentMaster: string,
  opts: RotationOpts,
): Promise<RotationReport> {
  const master = useMasterSession.getState().master;
  if (!master?.appConfig) {
    throw new Error("no master session unlocked");
  }

  const oldBytes = toPassphraseBytes(oldMaster);
  const newBytes = toPassphraseBytes(currentMaster);
  const startedAt = Math.floor(Date.now() / 1000);

  try {
    const cfg = master.appConfig;
    const filter = opts.vaultIds;
    const targets = cfg.vaults.filter((v) => {
      if (v.sync_state !== "synced") return false;
      if (v.pending_rotation == null) return false;
      if (filter && filter.length > 0 && !filter.includes(v.id)) return false;
      return true;
    });
    const results: RotationResultRow[] = targets.map((v) => ({
      vaultId: v.id,
      displayName: v.display_name,
      outcome: "pending",
    }));

    await rotateAllTargets(worker, targets, oldBytes, newBytes, results, opts);

    return await commitRotationResults({
      worker,
      master,
      cfg,
      results,
      startedAt,
      opts,
      masterRotated: false,
    });
  } finally {
    zeroize(oldBytes);
    zeroize(newBytes);
  }
}
